import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";
import {
  getMeeting,
  getMeetingPrep,
  getPreMeetingSuggestedParticipants,
  GET_MEETING_PREP,
  MEETING_GET,
  PRE_MEETING_SUGGESTED_PARTICIPANTS_GET,
} from "@/api";
import {
  Client,
  MeetingInvitee,
  Participant,
  SuggestedParticipants,
  User,
} from "@/types";
import { getPrimaryEmail } from "@/lib";
import { showErrorToast } from "@/lib/errorUtils";
import { UpdateParticipantData } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag";
import { useSessionContext } from "@/context";

interface UsePreMeetingDetailsOptions {
  /** CalendarEventSelector tri-state: null = unresolved, "" = explicit "No calendar event", id = event. */
  selectedMeetingId: string | null;
  /** Whether a calendar integration is connected — gates the suggestions fetch. */
  hasCalendar: boolean;
}

const getDraftParticipantId = (p: Partial<Participant>): string =>
  p.clientId
    ? `client:${p.clientId}`
    : p.userId
      ? `user:${p.userId}`
      : `email:${(p.email || "").toLowerCase()}`;

const inviteeToDraftParticipant = (
  invitee: MeetingInvitee,
): Partial<Participant> => {
  const draft: Partial<Participant> = {
    name: invitee.name || "",
    email: invitee.email,
  };
  // Unmatched external attendees arrive with empty-string ids — treat as absent.
  if (invitee.clientId) draft.clientId = invitee.clientId;
  if (invitee.userId) draft.userId = invitee.userId;
  draft.id = getDraftParticipantId(draft);
  return draft;
};

const clientToDraftParticipant = (client: Client): Partial<Participant> => {
  const draft: Partial<Participant> = {
    name: `${client.firstname ?? ""} ${client.lastname ?? ""}`.trim(),
    email: getPrimaryEmail(client),
    clientId: client.id,
    client,
  };
  draft.id = getDraftParticipantId(draft);
  return draft;
};

export const usePreMeetingDetails = ({
  selectedMeetingId,
  hasCalendar,
}: UsePreMeetingDetailsOptions) => {
  const [title, setTitle] = useState("");
  const [participants, setParticipants] = useState<Partial<Participant>[]>([]);
  // Which meeting id the fields were last populated from; null = pristine.
  // State (not a ref) so consumers can react to the populated/pristine flag.
  const [populatedMeetingId, setPopulatedMeetingId] = useState<string | null>(
    null,
  );
  const prevSelectedMeetingIdRef = useRef<string | null>(selectedMeetingId);

  // "" (explicit no-event) fetches nothing.
  const meetingId = selectedMeetingId || null;

  // Distinct key on purpose: [MEETING_GET, meetingId] is fetched elsewhere
  // WITHOUT includeInvitees — sharing it makes invitees nondeterministically
  // absent depending on which fetch wins the cache.
  const {
    data: meeting,
    error: meetingError,
    isError: isMeetingError,
  } = useQuery({
    queryKey: [MEETING_GET, "pre-meeting", meetingId],
    queryFn: () => getMeeting(meetingId, { includeInvitees: true }),
    enabled: !!meetingId,
  });

  // Meeting-prep clients belong in the reviewable list too — parity with the
  // fallback derivation in useCreateParticipantsFromMeeting.
  const { data: meetingPrep, isError: isMeetingPrepError } = useQuery({
    queryKey: [GET_MEETING_PREP, meeting?.meetingPrepId],
    queryFn: () => getMeetingPrep(meeting?.meetingPrepId),
    enabled: !!meeting?.meetingPrepId,
  });

  // A failed fetch must not read as "no participants" — surface it; callers
  // also fall back to the legacy derivation while nothing was populated.
  useEffect(() => {
    if (!isMeetingError) return;
    showErrorToast(
      meetingError,
      "conversation",
      "Failed to load calendar event details. Participants won't be pre-filled.",
    );
  }, [isMeetingError, meetingError]);

  // Acceptance criterion 1.5: switching events replaces both fields. Wipe on
  // any selection change; the populate effect refills once the meeting loads.
  useEffect(() => {
    if (prevSelectedMeetingIdRef.current === selectedMeetingId) return;
    prevSelectedMeetingIdRef.current = selectedMeetingId;
    setTitle("");
    setParticipants([]);
    setPopulatedMeetingId(null);
  }, [selectedMeetingId]);

  // Populate once per distinct meeting id, so same-id refetches (websocket
  // invalidation, focus refetch) never clobber user edits. When the meeting
  // has a prep, wait for it (unless it errored) so prep clients land in the
  // same populate pass. Drafts are deduped by id and email — one client
  // matched via several invitee emails must produce one chip.
  useEffect(() => {
    if (!meeting) return;
    if (meeting.id !== meetingId) return;
    if (populatedMeetingId === meeting.id) return;
    if (meeting.meetingPrepId && !meetingPrep && !isMeetingPrepError) return;

    setPopulatedMeetingId(meeting.id);
    setTitle(meeting.title || "");

    const drafts: Partial<Participant>[] = [];
    const seenIds = new Set<string>();
    const seenEmails = new Set<string>();
    const push = (draft: Partial<Participant>) => {
      const email = (draft.email || "").toLowerCase();
      if (!draft.id || seenIds.has(draft.id)) return;
      if (email && seenEmails.has(email)) return;
      seenIds.add(draft.id);
      if (email) seenEmails.add(email);
      drafts.push(draft);
    };
    (meeting.invitees || []).map(inviteeToDraftParticipant).forEach(push);
    (meetingPrep?.clients || []).map(clientToDraftParticipant).forEach(push);
    setParticipants(drafts);
  }, [meeting, meetingId, meetingPrep, isMeetingPrepError, populatedMeetingId]);

  const addParticipant = useCallback((data: UpdateParticipantData) => {
    const draft: Partial<Participant> = {
      name: `${data.data.firstname ?? ""} ${data.data.lastname ?? ""}`.trim(),
      email:
        data.type === "user"
          ? (data.data as User).email
          : getPrimaryEmail(data.data as Client),
    };
    if (data.type === "client") {
      draft.clientId = data.data.id;
      draft.client = data.data as Client;
    } else {
      draft.userId = data.data.id;
      draft.user = data.data as User;
    }
    draft.id = getDraftParticipantId(draft);
    setParticipants((prev) => {
      const email = (draft.email || "").toLowerCase();
      const exists = prev.some(
        (p) =>
          p.id === draft.id ||
          (!!email && (p.email || "").toLowerCase() === email),
      );
      return exists ? prev : [...prev, draft];
    });
  }, []);

  const removeParticipant = useCallback((participantId: string) => {
    setParticipants((prev) => prev.filter((p) => p.id !== participantId));
  }, []);

  // Suggestions come from GET /suggested-participant (backend-ranked:
  // nearest meeting first, organizer first, deduped — never re-sort).
  // Meeting mode when an event is selected; otherwise the user's local
  // calendar day in window mode (the server has no default day — only the
  // browser knows the user's timezone). The bounds change only at local
  // midnight, so the query key stays stable within a day.
  const { userId: currentUserId } = useSessionContext();
  const dayStart = startOfDay(new Date());
  const startAt = dayStart.getTime();
  const endAt = addDays(dayStart, 1).getTime();

  // Failures are deliberately silent (no toast): suggestions are a
  // progressive enhancement — an error just renders no "Suggested match"
  // section, same as an empty result. No fetch while the selection is
  // unresolved (null): the details fields aren't rendered then, and the
  // window-mode result would be wasted if the user picks a specific event.
  const { data: fetchedSuggestions } = useQuery({
    queryKey: [
      PRE_MEETING_SUGGESTED_PARTICIPANTS_GET,
      meetingId ?? `${startAt}-${endAt}`,
    ],
    queryFn: () =>
      getPreMeetingSuggestedParticipants(
        meetingId ? { meetingId } : { startAt, endAt },
      ),
    enabled: hasCalendar && selectedMeetingId !== null,
    staleTime: 5 * 60 * 1000,
  });

  // The server may include the current user (e.g. as organizer) — never
  // suggest the user to themself. Already-added participants are excluded
  // downstream by useUnclaimedSuggestions; unresolved ids are dropped by
  // SuggestedParticipantsSection. Mirror the query gates: a disabled query
  // still exposes cached data (e.g. after a calendar disconnect).
  const suggestedParticipants: SuggestedParticipants = useMemo(() => {
    if (!hasCalendar || selectedMeetingId === null) return [];
    if (!fetchedSuggestions) return [];
    return fetchedSuggestions.filter(
      (s) => !s.userId || s.userId !== currentUserId,
    );
  }, [hasCalendar, selectedMeetingId, fetchedSuggestions, currentUserId]);

  const reset = useCallback(() => {
    setTitle("");
    setParticipants([]);
    setPopulatedMeetingId(null);
  }, []);

  return {
    title,
    setTitle,
    participants,
    addParticipant,
    removeParticipant,
    suggestedParticipants,
    meeting,
    reset,
    /** The fields currently hold the selected meeting's data (prefill done). */
    isPopulated:
      populatedMeetingId !== null && populatedMeetingId === meetingId,
    /** The selected meeting failed to load — nothing was pre-filled. */
    isMeetingLoadError: isMeetingError,
  };
};
